import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, Filter, Grid, List, AlignLeft, Building2 } from 'lucide-react';
import Header from '../components/Header';
import CategoryCard from '../components/CategoryCard';
import InfoSlider from '../components/InfoSlider';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../context/BrandingContext';
import apiService from '../services/apiService';

const DashboardPage = ({ onCategorySelect }) => {
  const { t } = useTranslation();
  const { user, isGlobalAdmin, currentTenant } = useAuth();
  const { getCurrentBranding, getThemeClasses } = useBranding();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const currentBranding = getCurrentBranding();

  // Obtener categorías y productos
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Si no hay usuario autenticado, usar datos de muestra
        if (!user) {
          setCategories([
            {
              id: 1,
              name: 'Tours Santiago',
              description: 'Tours por la capital de Chile',
              products: 2,
              icon: 'Building2'
            },
            {
              id: 2,
              name: 'Valparaíso y Viña',
              description: 'Tours por la costa chilena',
              products: 3,
              icon: 'MapPin'
            }
          ]);
          return;
        }
        
        const [categoriesData, productsData] = await Promise.all([
          apiService.getCategories(),
          apiService.getProducts()
        ]);
        
        // Filtrar solo categorías padre (excluir subcategorías)
        const parentCategories = (categoriesData || []).filter(cat => !cat.parent_id);
        
        // Contar productos activos por categoría (incluir productos de subcategorías)
        const activeProducts = (productsData || []).filter(p => p.is_active);
        const categoriesWithCount = parentCategories
          .map(category => {
            // Buscar subcategorías de esta categoría
            const subcategories = (categoriesData || []).filter(cat => cat.parent_id === category.id);
            const subcategoryIds = subcategories.map(sub => sub.id);
            
            // Contar productos de la categoría padre y sus subcategorías
            const productCount = activeProducts.filter(p => 
              p.category_id === category.id || subcategoryIds.includes(p.category_id)
            ).length;
            
            return {
              ...category,
              products: productCount,
              subcategories: subcategories
            };
          })
          .sort((a, b) => (a.order_index || 0) - (b.order_index || 0)); // Ordenar por order_index
        
        setCategories(categoriesWithCount);
        setProducts(activeProducts);
      } catch (error) {
        console.error('Error loading data:', error);
        // Usar datos de fallback si falla la API
        setCategories([
          {
            id: 1,
            name: 'Tours Santiago',
            description: 'Tours por la capital de Chile',
            products: 2,
            icon: 'Building2'
          },
          {
            id: 2,
            name: 'Valparaíso y Viña',
            description: 'Tours por la costa chilena', 
            products: 3,
            icon: 'MapPin'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentTenant, user?.id]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCompanyName = () => {
    if (isGlobalAdmin) {
      return 'Todas las Empresas';
    }
    return currentBranding.name;
  };

  return (
    <div className={`min-h-screen ${getThemeClasses().headerBg}`}>
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        {/* Título principal */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-800">
              {isGlobalAdmin ? 'Panel de Administración Global' : `Bienvenido, ${user?.name}`}
            </h1>
          </div>
          {isGlobalAdmin && (
            <p className="text-gray-600 text-lg">
              Gestiona todas las empresas y categorías del sistema
            </p>
          )}
          {isGlobalAdmin && (
            <div className="mt-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg inline-block">
              <span className="font-medium">Modo Administrador</span> - Visualizando todas las categorías
            </div>
          )}
        </motion.div>

        {/* Slider de información */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <InfoSlider />
        </motion.div>

        {/* Barra de búsqueda y controles */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-8"
        >
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('common.search')}
              className="w-full sm:w-80 pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex gap-2">
            <button className="flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">{t('common.filter')}</span>
            </button>
            
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}
                title="Cuadrícula"
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}
                title="Lista"
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('detailed')}
                className={`p-2 rounded-lg ${viewMode === 'detailed' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}
                title="Lista Detallada"
              >
                <AlignLeft className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Información de contexto */}
        {!isGlobalAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg"
          >
            <p className="text-blue-700 text-sm">
              <strong>Tarifario</strong> • 
              <span className="ml-2">Categorías disponibles: {categories.length}</span>
            </p>
          </motion.div>
        )}

        {/* Grid de categorías */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('messages.noResults')}</h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? `No se encontraron categorías que coincidan con "${searchTerm}"`
                  : 'Aún no se han configurado categorías para esta empresa'
                }
              </p>
            </div>
          ) : (
            <>
              <div className={
                viewMode === 'grid' 
                  ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" 
                  : viewMode === 'detailed'
                  ? "space-y-6"
                  : "space-y-4"
              }>
                {filteredCategories.map((category, index) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                  >
                    <CategoryCard 
                      category={category} 
                      onClick={() => onCategorySelect && onCategorySelect(category)}
                      viewMode={viewMode}
                      index={index}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Botones Marketing Kit y Guía de Viajes */}
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                {getCurrentBranding().marketingKitUrl && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                  >
                    <a
                      href={getCurrentBranding().marketingKitUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-8 py-4 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                      style={{
                        backgroundColor: getCurrentBranding().buttonMarketingKitColor || '#10B981'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonMarketingKitColor || '#10B981'}dd`}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonMarketingKitColor || '#10B981'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Accede al Marketing Kit
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </motion.div>
                )}
                {getCurrentBranding().travelGuideUrl && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                  >
                    <a
                      href={getCurrentBranding().travelGuideUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-8 py-4 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                      style={{
                        backgroundColor: getCurrentBranding().buttonTravelGuideColor || '#8B5CF6'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonTravelGuideColor || '#8B5CF6'}dd`}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonTravelGuideColor || '#8B5CF6'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Accede a la Guía de Viajes
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </motion.div>
                )}
                {getCurrentBranding().descriptivesUrl && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0 }}
                  >
                    <a
                      href={getCurrentBranding().descriptivesUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-8 py-4 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                      style={{
                        backgroundColor: getCurrentBranding().buttonDescriptivesColor || '#F59E0B'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${getCurrentBranding().buttonDescriptivesColor || '#F59E0B'}dd`}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = getCurrentBranding().buttonDescriptivesColor || '#F59E0B'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Accede a los Descriptivos
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </motion.div>
                )}
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;